import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateCar } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Car, TireInput, TireSeason } from '../../types';
import { TIRE_SEASONS } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { t } from '../../i18n';

type Values = Record<keyof TireInput, string>;

export function TireFormModal({ car, onClose }: { car: Car | null; onClose: () => void }) {
  const update = useUpdateCar();
  const { register, handleSubmit, reset } = useForm<Values>();

  useLayoutEffect(() => {
    if (!car) return;
    reset({
      tire_season: car.tire_season ?? '',
      tire_brand: car.tire_brand ?? '',
      tire_model: car.tire_model ?? '',
      tire_size: car.tire_size ?? '',
      tires_mounted_on: car.tires_mounted_on ?? '',
    });
  }, [car, reset]);

  const onSubmit = async (v: Values) => {
    if (!car) return;
    const data: TireInput = {
      tire_season: (v.tire_season || null) as TireSeason | null,
      tire_brand: v.tire_brand.trim() || null,
      tire_model: v.tire_model.trim() || null,
      tire_size: v.tire_size.trim() || null,
      tires_mounted_on: v.tires_mounted_on || null,
    };
    try {
      await update.mutateAsync({ id: car.id, data });
      toast.success(t('Pneumatiky {plate} boli uložené', { plate: car.license_plate }));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Pneumatiky sa nepodarilo uložiť')));
    }
  };

  return (
    <Modal
      isOpen={!!car}
      onClose={onClose}
      busy={update.isPending}
      title={car ? `Pneumatiky – ${car.license_plate}` : ''}
      description={t('Aktuálne namontovaná sada. Pri prezutí ju prepíšte – predchádzajúca zostane v histórii zmien vozidla.')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={update.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="tire-form" loading={update.isPending}>
            {t('Uložiť')}
          </Button>
        </>
      }
    >
      <form id="tire-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <Select
          label={t('Typ pneumatík')}
          placeholder={t('Nezadané')}
          options={(Object.keys(TIRE_SEASONS) as TireSeason[]).map((s) => ({ value: s, label: TIRE_SEASONS[s].label }))}
          {...register('tire_season')}
        />
        <Input label={t('Namontované dňa')} type="date" {...register('tires_mounted_on')} />
        <Input label={t('Značka')} placeholder={t('napr. Michelin')} {...register('tire_brand')} />
        <Input label={t('Model / dezén')} placeholder={t('napr. Alpin 6')} {...register('tire_model')} />
        <Input label={t('Rozmer')} placeholder={t('napr. 205/55 R16 91H')} wrapperClassName="sm:col-span-2" {...register('tire_size')} />
      </form>
    </Modal>
  );
}
